import { Chips } from '@cian/ui-kit';

import { type FC, useCallback, useState } from 'react';

import { type IChipsDataService } from 'shared/services/ChipsDataService';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { SliderBlock } from '../SliderBlock';
import { VerticalArrow } from '../VerticalArrow';

import { type ISliderBlockTypeOneModel } from './external/models/SliderBlockTypeOneModel';
import { Slide } from './internal/components/Slide';

type SliderBlockTypeOneProps = {
  id: string;
  title: string;
  description: string;
  sliderBlockTypeOneSlideService: IChipsDataService<ISliderBlockTypeOneModel>;
};

/**
 * @description Слайдер типа один. Слайдер представляет собой виджет, который содержит в себе список слайдов и чипы для выбора набора слайдов.
 * Сам слайд содержит в себе изображение снизу, заголовок и описание сверху.
 * @param id - Идентификатор слайдера.
 * @param title - Заголовок слайдера.
 * @param description - Описание слайдера.
 * @param sliderBlockTypeOneSlideService - Сервис данных слайдера.
 */
export const SliderBlockTypeOne: FC<SliderBlockTypeOneProps> = ({
  id,
  title,
  description,
  sliderBlockTypeOneSlideService,
}) => {
  const { logger } = useApplicationContext();

  const [activeDefaultSliderBlockSlide, setActiveDefaultSliderBlockSlide] = useState<ISliderBlockTypeOneModel>(
    sliderBlockTypeOneSlideService.defaultItem,
  );

  const handleChange = useCallback<(value: string[]) => void>(
    value => {
      setActiveDefaultSliderBlockSlide(sliderBlockTypeOneSlideService.getActiveItem(value[0], logger));
    },
    [logger, sliderBlockTypeOneSlideService],
  );

  return (
    <SliderBlock
      arrow={VerticalArrow}
      contentOffset={null}
      description={description}
      id={id}
      rightAdornment={
        <Chips<string>
          dataTestId={`${id}-chips`}
          options={sliderBlockTypeOneSlideService.chipsOptions}
          value={[activeDefaultSliderBlockSlide.chipsOption.value]}
          onChange={handleChange}
        />
      }
      sliderKey={activeDefaultSliderBlockSlide.chipsOption.value}
      title={title}
    >
      {activeDefaultSliderBlockSlide.slides.map((slide, index) => (
        <Slide description={slide.description} imageUrl={slide.imageUrl} key={index} title={slide.title} />
      ))}
    </SliderBlock>
  );
};
